import React, { useState, useEffect, useRef } from 'react';
import { 
    BrainCircuit, Target, Users, TrendingUp, Star, Quote,
    AlertTriangle, CheckCircle, Sparkles, Film, Upload, FileText,
    Camera, Mic, Download, ScanEye, Loader2, FileSpreadsheet,
    BookOpen, Layers, UserCircle, ChevronRight, Zap, Share2,
    Megaphone, DollarSign, CalendarDays, Activity, Tag, Clock,
    Radio, BarChart2
} from 'lucide-react';

import { 
    AppCard, AppButton, SectionTitle, BlackboxLoaderIcon, AppTextarea 
} from '../../components/SharedUI';

import { 
    generateEnhancedScriptAnalysisLLM, 
    generateCharacterAnalysisLLM,
    generateSocialMediaPlaybookLLM,
    compileCalendarStrategyJSON,
    compileCampaignExportJSON
} from '../../services/aiService';

// --- NAV TAB ---
const NavTab = ({ label, icon: Icon, isActive, onClick }) => (
    <button 
        onClick={onClick}
        className={`flex items-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-all ${isActive ? 'border-amber-500 text-white bg-amber-500/5' : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/50'}`}
    >
        <Icon size={16} className={isActive ? "text-amber-400" : ""} />
        {label}
    </button>
);

// --- LOADING OVERLAY ---
const IntelligenceOverlay = ({ isVisible, mode, currentStepIndex }) => {
    if (!isVisible) return null;

    const steps = {
        script: [
            "Ingesting Script Manuscript...",
            "Mapping Scenes to Andromeda Hooks...",
            "Scoring Emotional Register Diversity...",
            "Extracting Algorithm-Ready Dialogue...",
            "Synthesizing Story Intelligence Report..."
        ],
        character: [
            "Parsing Character Entrances & Arcs...",
            "Mapping Characters to Creative Matrix Hooks...",
            "Scoring Algorithmic Signal Strength...",
            "Identifying Signature Algorithm Moments...",
            "Finalizing Character Intel Dossiers..."
        ],
        social: [
            "Analyzing Script for Capture Opportunities...",
            "Building Phase Timeline Against Andromeda Calendar...",
            "Generating BTS Capture Manifest...",
            "Modeling Paid Promotion Drop Strategy...",
            "Finalizing Social Media Playbook..."
        ]
    };

    const activeSteps = steps[mode] || steps.script;
    const progress = Math.min(((currentStepIndex + 1) / activeSteps.length) * 100, 100);
    const accentColor = mode === 'social' ? 'bg-amber-500' : 'bg-purple-600';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/95 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className={`absolute inset-0 blur-2xl opacity-20 animate-pulse rounded-full ${accentColor}`} />
                        <BrainCircuit className="w-20 h-20 text-white relative z-10 animate-pulse" />
                    </div>
                </div>
                <div className="w-full h-1 bg-zinc-800 rounded-full mb-8 overflow-hidden">
                    <div className={`h-full transition-all duration-500 ease-out ${accentColor}`} style={{ width: `${progress}%` }} />
                </div>
                <div className="space-y-3 font-mono text-xs">
                    {activeSteps.map((step, index) => {
                        const isDone = index < currentStepIndex;
                        const isCurrent = index === currentStepIndex;
                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${isCurrent ? 'text-white font-bold' : isDone ? 'text-zinc-600' : 'text-zinc-700'}`}>
                                <div className="w-5 flex justify-center shrink-0">
                                    {isDone    ? <CheckCircle size={14} className="text-green-500" /> 
                                     : isCurrent ? <Loader2 size={14} className="animate-spin" /> 
                                     : <div className="w-3 h-3 rounded-full border border-zinc-700" />}
                                </div>
                                <span>{step}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- HOOK BADGE ---
const HookBadge = ({ hook }) => {
    if (!hook) return null;
    return (
        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-300 border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 rounded-full truncate max-w-[180px]" title={hook}>
            {hook}
        </span>
    );
};

// --- FORMAT BADGE ---
const FormatBadge = ({ format }) => {
    const map = {
        'Vertical Video': 'text-blue-300 border-blue-500/30 bg-blue-500/10',
        'Carousel':       'text-purple-300 border-purple-500/30 bg-purple-500/10',
        'Static Image':   'text-teal-300 border-teal-500/30 bg-teal-500/10',
    };
    const cls = map[format] || 'text-zinc-400 border-zinc-700 bg-zinc-800';
    return (
        <span className={`text-[9px] font-bold uppercase tracking-wider border px-2 py-0.5 rounded-full ${cls}`}>
            {format}
        </span>
    );
};

// ─────────────────────────────────────────────────────────────
// STORY INTEL VIEW
// ─────────────────────────────────────────────────────────────
const StoryIntelView = ({ analysis, filmTitle, setNotification }) => {
    if (!analysis) return (
        <div className="p-20 text-center border-2 border-dashed border-zinc-800 rounded-2xl">
            <Layers size={40} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-white font-bold mb-1">No Story Intelligence Yet</h3>
            <p className="text-zinc-500 text-sm">Upload a script and run Analyze Story to generate the Andromeda hook map.</p>
        </div>
    );

    const handleExportPDF = () => {
        try {
            if (!window.jspdf) { setNotification({ type: 'error', message: 'PDF generator not loaded.' }); return; }
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 50;
            let y = 0;

            const checkPage = (needed) => { if (y + needed > pageHeight - margin) { doc.addPage(); y = 50; } };

            // Cover
            doc.setFillColor(15, 15, 18);
            doc.rect(0, 0, pageWidth, pageHeight, 'F');
            doc.setFillColor(245, 158, 11);
            doc.rect(0, 0, 6, pageHeight, 'F');
            doc.setTextColor(245, 158, 11); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
            doc.text("BLACKBOX — ANDROMEDA STORY FORENSICS", margin, 55);
            doc.setTextColor(255, 255, 255); doc.setFontSize(36);
            const titleLines = doc.splitTextToSize((filmTitle || "UNTITLED").toUpperCase(), pageWidth - margin * 2);
            doc.text(titleLines, margin, 230);
            doc.setFont("helvetica", "normal"); doc.setFontSize(12); doc.setTextColor(113, 113, 122);
            doc.text("CAMPAIGN STORY FORENSICS & HOOK MAP", margin, 230 + titleLines.length * 42);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, pageHeight - 40);

            doc.addPage(); y = 60;

            // Hooks
            doc.setTextColor(0, 0, 0); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
            doc.text("I. ANDROMEDA CAMPAIGN HOOKS", margin, y); y += 25;
            doc.setDrawColor(245, 158, 11); doc.setLineWidth(2); doc.line(margin, y, pageWidth - margin, y); y += 25;
            (analysis.campaign_hooks?.secondary_hooks || []).forEach(hook => {
                checkPage(50);
                doc.setFillColor(254, 252, 232);
                const hLines = doc.splitTextToSize(hook, pageWidth - margin * 2 - 20);
                doc.roundedRect(margin, y - 12, pageWidth - margin * 2, hLines.length * 14 + 18, 4, 4, 'F');
                doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(40, 40, 40);
                doc.text(hLines, margin + 10, y); y += hLines.length * 14 + 25;
            });

            y += 20; checkPage(80);
            doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.setTextColor(0, 0, 0);
            doc.text("II. ANDROMEDA MOMENT MAP", margin, y); y += 25;
            doc.setDrawColor(245, 158, 11); doc.line(margin, y, pageWidth - margin, y); y += 25;

            (analysis.marketable_moments || []).forEach((item, i) => {
                checkPage(90);
                doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(200, 200, 200);
                doc.text(`0${i + 1}`, margin, y + 16);
                doc.setFontSize(11); doc.setTextColor(0, 0, 0);
                const mLines = doc.splitTextToSize(item.scene_description, pageWidth - margin - 80);
                doc.text(mLines, margin + 42, y);
                y += mLines.length * 14 + 6;
                doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 80, 0);
                doc.text(`HOOK: ${item.andromeda_hook || '—'}   FORMAT: ${item.best_format || '—'}   REGISTER: ${item.emotional_register || '—'}`, margin + 42, y);
                y += 30;
            });

            doc.save(`${filmTitle}_Andromeda_Story_Forensics.pdf`);
            setNotification({ type: 'success', message: 'Story Forensics PDF exported.' });
        } catch (e) {
            console.error(e); setNotification({ type: 'error', message: 'PDF export failed.' });
        }
    };

    const moments = analysis.marketable_moments || [];
    const hooks = analysis.campaign_hooks?.secondary_hooks || [];

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex justify-between items-center bg-zinc-900/40 p-6 rounded-2xl border border-zinc-800">
                <div>
                    <h3 className="text-xl font-bold text-white">Andromeda Story Forensics</h3>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">Hook Map · Moment Register · Campaign Signals</p>
                </div>
                <AppButton onClick={handleExportPDF} variant="primary" size="sm" icon={Download}>Export PDF</AppButton>
            </div>

            {/* Hook Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AppCard className="p-6 border-l-4 border-amber-500 bg-zinc-900/20">
                    <h3 className="text-xs font-bold text-zinc-400 mb-4 flex items-center gap-2 uppercase tracking-widest">
                        <Zap size={12} className="text-amber-400" /> Andromeda Campaign Hooks
                    </h3>
                    <ul className="space-y-3">
                        {hooks.map((hook, i) => (
                            <li key={i} className="flex gap-3 text-xs text-zinc-300 leading-relaxed">
                                <span className="text-amber-400 font-bold shrink-0">{String(i+1).padStart(2,'0')}</span> {hook}
                            </li>
                        ))}
                        {hooks.length === 0 && <li className="text-zinc-600 italic text-xs">No hooks extracted.</li>}
                    </ul>
                </AppCard>

                <AppCard className="p-6 border-l-4 border-red-500 bg-zinc-900/20">
                    <h3 className="text-xs font-bold text-zinc-400 mb-4 flex items-center gap-2 uppercase tracking-widest">
                        <AlertTriangle size={12} className="text-red-500" /> Campaign Risk Flags
                    </h3>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        {analysis.risk_assessment?.marketing_challenges?.join(' ') || "No risk flags identified."}
                    </p>
                </AppCard>
            </div>

            {/* Moment Map */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Andromeda Moment Map</h3>
                    <span className="text-[10px] text-zinc-600 font-mono">{moments.length} moments identified</span>
                </div>
                <div className="space-y-3">
                    {moments.map((item, i) => (
                        <div key={i} className="group bg-zinc-950 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/30 transition-all">
                            <div className="flex gap-5 items-start">
                                <div className="text-2xl font-black text-zinc-800 group-hover:text-amber-900/40 shrink-0 transition-colors">
                                    {String(i+1).padStart(2,'0')}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                                        <h4 className="font-bold text-white text-sm flex-1">{item.scene_description}</h4>
                                        {item.trailer_worthy && (
                                            <span className="bg-green-500/10 text-green-400 text-[9px] font-bold px-2 py-0.5 rounded border border-green-500/20 uppercase shrink-0">
                                                Trailer Asset
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <HookBadge hook={item.andromeda_hook} />
                                        <FormatBadge format={item.best_format} />
                                        {item.emotional_register && (
                                            <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 border border-zinc-700 bg-zinc-900 px-2 py-0.5 rounded-full">
                                                {item.emotional_register}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-4 text-[10px] text-zinc-500">
                                        <span><span className="text-zinc-600">Target:</span> {item.target_demographic}</span>
                                        {item.page_reference && <span><span className="text-zinc-600">Pg:</span> {item.page_reference}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    {moments.length === 0 && (
                        <div className="p-10 text-center border border-dashed border-zinc-800 rounded-xl text-zinc-600 text-sm italic">
                            No moments extracted yet.
                        </div>
                    )}
                </div>
            </div>

            {/* Quotable Dialogue */}
            {analysis.quotable_dialogue?.length > 0 && (
                <div>
                    <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Algorithm-Ready Dialogue</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {analysis.quotable_dialogue.map((item, i) => (
                            <div key={i} className="p-4 bg-zinc-900/30 border border-zinc-800 rounded-xl hover:border-amber-500/20 transition-colors">
                                <div className="flex gap-3 mb-3">
                                    <Quote size={14} className="text-amber-500/50 shrink-0 mt-0.5" />
                                    <p className="text-sm text-zinc-200 italic font-serif leading-relaxed">"{item.quote}"</p>
                                </div>
                                <div className="flex flex-wrap gap-2 items-center border-t border-zinc-800 pt-2">
                                    <span className="text-[10px] text-zinc-500 font-bold">{item.character}</span>
                                    <span className="text-zinc-700">·</span>
                                    <span className="text-[10px] text-zinc-500">{item.social_media_potential}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// CHARACTER INTEL VIEW
// ─────────────────────────────────────────────────────────────
const CharacterIntelView = ({ data, filmTitle, setNotification }) => {
    const profiles = Array.isArray(data?.character_profiles) ? data.character_profiles : [];
    const [selectedChar, setSelectedChar] = useState(profiles[0] || null);

    useEffect(() => {
        if (profiles.length > 0 && !selectedChar) setSelectedChar(profiles[0]);
    }, [profiles, selectedChar]);

    if (profiles.length === 0) return (
        <div className="p-20 text-center bg-zinc-900/20 border border-dashed border-zinc-800 rounded-2xl">
            <UserCircle size={48} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-white font-bold mb-1">No Character Intelligence</h3>
            <p className="text-zinc-500 text-sm">Upload a script and run Profile Characters to generate Andromeda hook dossiers.</p>
        </div>
    );

    const handleExportCharacterPDF = (char) => {
        try {
            if (!window.jspdf) { setNotification({ type: 'error', message: 'PDF generator not loaded.' }); return; }
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 50; let y = 0;
            const checkPage = (n) => { if (y + n > pageHeight - margin) { doc.addPage(); y = 50; } };

            // Cover
            doc.setFillColor(15, 15, 18); doc.rect(0, 0, pageWidth, 150, 'F');
            doc.setFillColor(245, 158, 11); doc.rect(0, 0, 6, 150, 'F');
            doc.setTextColor(245, 158, 11); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
            doc.text("BLACKBOX — CHARACTER ANDROMEDA DOSSIER", margin, 38);
            doc.setTextColor(255, 255, 255); doc.setFontSize(30);
            doc.text(char.name.toUpperCase(), margin, 80);
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(113, 113, 122);
            doc.text(`PROJECT: ${(filmTitle || 'UNTITLED').toUpperCase()}   |   ARCHETYPE: ${char.archetype || 'N/A'}   |   SCORE: ${char.marketability_score || '?'}/10`, margin, 115);

            y = 185;

            const section = (title) => {
                checkPage(50); doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(0, 0, 0);
                doc.text(title, margin, y); y += 12;
                doc.setDrawColor(245, 158, 11); doc.setLineWidth(1.5); doc.line(margin, y, pageWidth - margin, y); y += 20;
            };

            section("I. ANDROMEDA SIGNAL");
            doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
            doc.text("HOOK:", margin, y);
            doc.setFont("helvetica", "normal"); doc.setTextColor(40, 40, 40);
            const hLines = doc.splitTextToSize(char.andromeda_hook || '—', pageWidth - margin * 2 - 40);
            doc.text(hLines, margin + 36, y); y += hLines.length * 12 + 8;
            doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
            doc.text("SIGNAL:", margin, y);
            doc.setFont("helvetica", "normal"); doc.setTextColor(40, 40, 40);
            doc.text(`${char.algorithm_signal_strength || '—'}   |   BEST FORMAT: ${char.best_format || '—'}`, margin + 44, y); y += 22;
            doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
            doc.text("SIGNATURE MOMENT:", margin, y); y += 14;
            doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
            const smLines = doc.splitTextToSize(char.signature_algorithm_moment || '—', pageWidth - margin * 2);
            doc.text(smLines, margin, y); y += smLines.length * 13 + 20;

            section("II. NARRATIVE ROI");
            const roiLines = doc.splitTextToSize(char.narrative_roi || char.character_arc || '—', pageWidth - margin * 2);
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
            doc.text(roiLines, margin, y); y += roiLines.length * 14 + 20;

            section("III. SIGNATURE DIALOGUE");
            char.best_quotes?.forEach(quote => {
                checkPage(50);
                doc.setFillColor(250, 250, 250);
                const qLines = doc.splitTextToSize(`"${quote}"`, pageWidth - margin * 2 - 20);
                doc.roundedRect(margin, y - 10, pageWidth - margin * 2, qLines.length * 14 + 18, 4, 4, 'F');
                doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(40, 40, 40);
                doc.text(qLines, margin + 10, y); y += qLines.length * 14 + 25;
            });

            doc.save(`${char.name}_Andromeda_Dossier.pdf`);
            setNotification({ type: 'success', message: `${char.name} Andromeda Dossier exported.` });
        } catch (e) { console.error(e); setNotification({ type: 'error', message: 'PDF export failed.' }); }
    };

    const signalColor = (s) => ({
        'High':   'bg-green-500/10 text-green-400 border-green-500/30',
        'Medium': 'bg-amber-500/10 text-amber-400 border-amber-500/30',
        'Low':    'bg-zinc-800 text-zinc-500 border-zinc-700',
    }[s] || 'bg-zinc-800 text-zinc-500 border-zinc-700');

    return (
        <div className="flex flex-col lg:flex-row gap-6 h-[75vh] animate-in fade-in duration-500">
            {/* Sidebar */}
            <div className="w-full lg:w-72 flex flex-col gap-2 overflow-y-auto pr-2 custom-scrollbar">
                <div className="px-2 mb-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex justify-between">
                    <span>Characters</span><span>Signal</span>
                </div>
                {profiles.map((char, i) => (
                    <button key={i} onClick={() => setSelectedChar(char)}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between gap-3 ${selectedChar?.name === char.name ? 'bg-amber-500/10 border-amber-500/40' : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'}`}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${selectedChar?.name === char.name ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                                {char.name?.charAt(0)}
                            </div>
                            <div className="min-w-0">
                                <h4 className={`text-sm font-bold truncate ${selectedChar?.name === char.name ? 'text-white' : 'text-zinc-400'}`}>{char.name}</h4>
                                <p className="text-[10px] text-zinc-600 uppercase tracking-tighter truncate">{char.archetype || 'Character'}</p>
                            </div>
                        </div>
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${signalColor(char.algorithm_signal_strength)}`}>
                            {char.algorithm_signal_strength || '?'}
                        </span>
                    </button>
                ))}
            </div>

            {/* Dossier */}
            <div className="flex-1 bg-zinc-900/40 border border-zinc-800 rounded-2xl p-8 overflow-y-auto custom-scrollbar">
                {selectedChar ? (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                        {/* Header */}
                        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-8 border-b border-zinc-800 pb-8">
                            <div>
                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <h2 className="text-3xl font-bold text-white tracking-tight">{selectedChar.name}</h2>
                                    <span className={`text-[10px] font-bold px-3 py-1 rounded-full border ${signalColor(selectedChar.algorithm_signal_strength)}`}>
                                        {selectedChar.algorithm_signal_strength || '?'} Signal
                                    </span>
                                    <span className="bg-zinc-800 text-zinc-400 text-[10px] font-bold px-2 py-1 rounded-full">
                                        {selectedChar.marketability_score || '?'}/10
                                    </span>
                                </div>
                                <p className="text-amber-400 font-bold uppercase text-xs tracking-widest">{selectedChar.archetype}</p>
                            </div>
                            <AppButton onClick={() => handleExportCharacterPDF(selectedChar)} variant="secondary" size="sm" icon={Download}>
                                Export Dossier
                            </AppButton>
                        </div>

                        {/* Andromeda Signal Block */}
                        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 mb-6">
                            <h4 className="text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <Zap size={11} /> Andromeda Signal Profile
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Hook Executed</p>
                                    <p className="text-xs text-amber-200 font-medium">{selectedChar.andromeda_hook || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Best Format</p>
                                    <FormatBadge format={selectedChar.best_format} />
                                </div>
                                <div>
                                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Audience Segment</p>
                                    <p className="text-xs text-zinc-300">{selectedChar.target_demographic || '—'}</p>
                                </div>
                            </div>
                            {selectedChar.signature_algorithm_moment && (
                                <div className="mt-4 pt-4 border-t border-amber-500/10">
                                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mb-2">Signature Algorithm Moment</p>
                                    <p className="text-xs text-zinc-200 italic leading-relaxed">"{selectedChar.signature_algorithm_moment}"</p>
                                </div>
                            )}
                        </div>

                        {/* Intel Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                            <div className="bg-zinc-950 p-5 rounded-xl border border-zinc-800">
                                <h4 className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Star size={11} className="text-amber-500" /> Casting Vibes
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedChar.casting_comparisons?.map((comp, i) => (
                                        <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-3 py-1.5 rounded-lg border border-zinc-700">{comp}</span>
                                    )) || <span className="text-zinc-600 text-xs italic">None identified.</span>}
                                </div>
                            </div>
                            <div className="bg-zinc-950 p-5 rounded-xl border border-zinc-800">
                                <h4 className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <BrainCircuit size={11} className="text-amber-500" /> Narrative ROI
                                </h4>
                                <p className="text-xs text-zinc-400 leading-relaxed italic">{selectedChar.narrative_roi || selectedChar.character_arc || '—'}</p>
                            </div>
                        </div>

                        {/* Marketing Angles */}
                        {selectedChar.marketing_angles?.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-3">Social Marketing Angles</h4>
                                <div className="flex flex-wrap gap-2">
                                    {selectedChar.marketing_angles.map((angle, i) => (
                                        <span key={i} className="text-xs bg-zinc-900 border border-zinc-800 text-zinc-300 px-3 py-1.5 rounded-lg">
                                            {angle}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Signature Dialogue */}
                        {selectedChar.best_quotes?.length > 0 && (
                            <div>
                                <h4 className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mb-3">Signature Dialogue</h4>
                                <div className="space-y-2">
                                    {selectedChar.best_quotes.map((quote, i) => (
                                        <div key={i} className="flex gap-3 p-4 bg-zinc-900/40 rounded-xl border border-zinc-800 hover:border-amber-500/20 transition-colors">
                                            <Quote size={14} className="text-zinc-700 shrink-0 mt-0.5" />
                                            <p className="text-zinc-300 text-sm italic font-serif">"{quote}"</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="h-full flex items-center justify-center text-zinc-600 italic">Select a character to view their Andromeda dossier.</div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// SOCIAL MEDIA PLAYBOOK VIEW
// ─────────────────────────────────────────────────────────────
const SocialPlaybookView = ({ data, filmTitle, filmData, audienceAnalysis, setNotification }) => {
    const [activeTab, setActiveTab] = useState('timeline');
    const [capturedItems, setCapturedItems] = useState(new Set());

    if (!data) return (
        <div className="p-20 text-center border-2 border-dashed border-zinc-800 rounded-2xl">
            <Megaphone size={40} className="mx-auto text-zinc-700 mb-4" />
            <h3 className="text-white font-bold mb-1">No Social Media Playbook Yet</h3>
            <p className="text-zinc-500 text-sm">Upload a script and run Generate Playbook to build your Andromeda campaign plan.</p>
        </div>
    );

    const toggleCapture = (id) => {
        const n = new Set(capturedItems);
        n.has(id) ? n.delete(id) : n.add(id);
        setCapturedItems(n);
    };

    const handleExportManifestCSV = () => {
        try {
            let csv = "data:text/csv;charset=utf-8,Moment,Capture Type,Hook,Audience Segment,Emotional Register,Paid Drop,Phase,Caption Direction\n";
            (data.bts_capture_manifest || []).forEach(item => {
                csv += `"${item.moment}","${item.capture_type}","${item.andromeda_hook}","${item.audience_segment}","${item.emotional_register}","${item.paid_drop ? 'YES' : 'NO'}","${item.posting_phase}","${item.caption_direction}"\n`;
            });
            const link = document.createElement('a');
            link.setAttribute('href', encodeURI(csv));
            link.setAttribute('download', `${filmTitle}_BTS_Manifest.csv`);
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            setNotification({ type: 'success', message: 'BTS Manifest exported to CSV.' });
        } catch (e) { setNotification({ type: 'error', message: 'CSV export failed.' }); }
    };

    // Compiles this Playbook into blackbox_calendar_strategy.json — the format
    // the Campaign Engine's "Add strategy brief" upload expects. Deterministic,
    // not another Gemini call — everything the calendar needs was already
    // written when the Playbook was generated.
    const handleExportCalendarJSON = () => {
        try {
            const compiled = compileCalendarStrategyJSON(filmData, audienceAnalysis, data);
            const blob = new Blob([JSON.stringify(compiled, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', 'blackbox_calendar_strategy.json');
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setNotification({ type: 'success', message: `Calendar compiled — ${compiled._meta.total_posts} posts across 3 phases. Upload it via "Add strategy brief" in the Campaign Engine.` });
        } catch (e) {
            // The compiler throws plain, specific messages (missing release date,
            // no playbook, etc.) rather than a generic failure — surface it as-is.
            setNotification({ type: 'error', message: e.message || 'Calendar export failed.' });
        }
    };

    
    const handleExportPlaybookPDF = () => {
        try {
            if (!window.jspdf || !data) {
                setNotification({ type: 'error', message: 'PDF engine not ready or missing data.' });
                return;
            }

            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 50;
            let y = margin;

            const C = {
                AMBER: [245, 158, 11],
                DARK: [24, 24, 27],
                GRAY: [113, 113, 122],
                BG_SHADE: [248, 250, 252],
                LINE: [226, 232, 240]
            };

            const checkPageBreak = (needed) => {
                if (y + needed > pageHeight - margin) {
                    doc.addPage();
                    y = margin;
                    return true;
                }
                return false;
            };

            const drawHeader = (title) => {
                checkPageBreak(60);
                y += 20;
                doc.setFillColor(...C.AMBER);
                doc.rect(margin, y, 4, 18, 'F');
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.setTextColor(...C.DARK);
                doc.text(title.toUpperCase(), margin + 12, y + 14);
                y += 35;
            };

            // 1. COVER HEADER
            doc.setFillColor(...C.DARK);
            doc.rect(0, 0, pageWidth, 120, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont("helvetica", "bold");
            doc.text("SOCIAL MEDIA PLAYBOOK", margin, 60);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(180, 180, 180);
            doc.text(`PROJECT: ${(filmTitle || "UNTITLED").toUpperCase()}`, margin, 85);

            y = 160;

            const addTextBlock = (text, indent = margin) => {
                if (!text) return;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(...C.DARK);
                const lines = doc.splitTextToSize(text, pageWidth - margin - indent);
                checkPageBreak(lines.length * 14 + 10);
                doc.text(lines, indent, y);
                y += (lines.length * 14) + 10;
            };

            const addLabelValue = (label, value, indent = margin) => {
                if (!value) return;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(...C.GRAY);
                doc.text(label.toUpperCase() + ":", indent, y);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...C.DARK);
                const vLines = doc.splitTextToSize(value, pageWidth - margin - indent - 80);
                doc.text(vLines, indent + 80, y);
                y += (vLines.length * 14) + 5;
            };

            // Paid Strategy
            if (data.paid_promotion_strategy) {
                drawHeader("Paid Promotion Strategy");
                doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...C.DARK);
                doc.text("PHILOSOPHY", margin, y); y += 15;
                addTextBlock(data.paid_promotion_strategy.philosophy);
                y += 10;
                
                if(data.paid_promotion_strategy.top_paid_drops?.length > 0) {
                    doc.setFont("helvetica", "bold"); doc.text("TOP PAID DROPS", margin, y); y += 15;
                    data.paid_promotion_strategy.top_paid_drops.forEach(drop => {
                        checkPageBreak(80);
                        doc.setFillColor(...C.BG_SHADE);
                        
                        doc.setFont("helvetica", "bold"); doc.setTextColor(...C.DARK); doc.setFontSize(10);
                        const dropLines = doc.splitTextToSize(`${drop.rank}. ${drop.moment}`, pageWidth - margin*2 - 10);
                        const boxHeight = (dropLines.length * 14) + 6;
                        
                        doc.rect(margin, y, pageWidth - margin*2, boxHeight, 'F');
                        doc.text(dropLines, margin + 5, y + 14);
                        y += boxHeight + 10;
                        
                        addLabelValue("Why", drop.why, margin + 5);
                        addLabelValue("Budget", drop.suggested_daily_budget, margin + 5);
                        addLabelValue("Metric", drop.target_metric, margin + 5);
                        y += 10;
                    });
                }
            }

            // Timeline
            if (data.release_timeline?.length > 0) {
                drawHeader("Release Timeline");
                data.release_timeline.forEach(phase => {
                    checkPageBreak(50);
                    doc.setFillColor(...C.DARK);
                    doc.rect(margin, y, pageWidth - margin*2, 20, 'F');
                    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
                    doc.text(phase.phase.toUpperCase(), margin + 5, y + 14);
                    y += 35;
                    
                    if (phase.strategic_goal) {
                        doc.setTextColor(...C.GRAY); doc.setFont("helvetica", "italic");
                        const sgLines = doc.splitTextToSize(phase.strategic_goal, pageWidth - margin*2);
                        doc.text(sgLines, margin, y); y += sgLines.length * 14 + 10;
                    }

                    phase.posts?.forEach((post, i) => {
                        checkPageBreak(80);
                        doc.setDrawColor(...C.LINE);
                        doc.line(margin + 5, y, margin + 5, y + 40);
                        doc.setFillColor(...C.AMBER);
                        doc.circle(margin + 5, y + 5, 3, 'F');

                        doc.setTextColor(...C.DARK); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
                        const postLines = doc.splitTextToSize(`${i+1}. ${post.content_idea}` + (post.paid_promotion ? " [PAID]" : ""), pageWidth - margin*2 - 20);
                        doc.text(postLines, margin + 15, y + 5);
                        y += (postLines.length * 14) + 4;
                        
                        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...C.GRAY);
                        doc.text(`FORMAT: ${post.format || 'N/A'}  |  HOOK: ${post.andromeda_hook || 'N/A'}`, margin + 15, y);
                        y += 15;
                        addTextBlock(post.caption_direction, margin + 15);
                        y += 10;
                    });
                });
            }

            // Manifest
            if (data.bts_capture_manifest?.length > 0) {
                drawHeader("Capture Manifest");
                data.bts_capture_manifest.forEach(item => {
                    checkPageBreak(80);
                    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...C.DARK);
                    
                    const momentLines = doc.splitTextToSize(`[ ] ${item.moment}` + (item.paid_drop ? " [PAID DROP]" : ""), pageWidth - margin*2);
                    doc.text(momentLines, margin, y);
                    y += (momentLines.length * 14) + 4;
                    
                    addLabelValue("Type", item.capture_type);
                    addLabelValue("Hook", item.andromeda_hook);
                    addLabelValue("Phase", item.posting_phase);
                    addLabelValue("Direction", item.caption_direction);
                    y += 10;
                    doc.setDrawColor(...C.LINE); doc.line(margin, y, pageWidth-margin, y); y += 15;
                });
            }

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(...C.GRAY);
                doc.text(`BLACKBOX Campaign Intelligence | Confidential | Page ${i}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
            }

            doc.save(`${filmTitle ? filmTitle.replace(/\s+/g, '_') : 'Project'}_Social_Playbook.pdf`);
            setNotification({ type: 'success', message: 'Social Playbook PDF exported.' });
        } catch (error) {
            console.error("PDF Error:", error);
            setNotification({ type: 'error', message: 'PDF Export failed.' });
        }
    };

    const handleCopyPlaybook = () => {
        const text = [
            `SOCIAL MEDIA PLAYBOOK: ${filmTitle}`,
            '',
            '── PAID PROMOTION PHILOSOPHY ──',
            data.paid_promotion_strategy?.philosophy || '',
            '',
            '── TOP PAID DROPS ──',
            ...(data.paid_promotion_strategy?.top_paid_drops || []).map(d =>
                `${d.rank}. ${d.moment}\n   WHY: ${d.why}\n   BUDGET: ${d.suggested_daily_budget}\n   METRIC: ${d.target_metric}`
            ),
            '',
            '── BTS CAPTURE MANIFEST ──',
            ...(data.bts_capture_manifest || []).map(item =>
                `[ ] ${item.moment} (${item.capture_type}) | Hook: ${item.andromeda_hook} | ${item.paid_drop ? 'PAID DROP' : 'Organic'}`
            )
        ].join('\n');
        navigator.clipboard.writeText(text);
        setNotification({ type: 'success', message: 'Playbook copied to clipboard.' });
    };

    const SubTab = ({ id, label, icon: Icon }) => (
        <button onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeTab === id ? 'bg-amber-500 text-black' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}
        >
            <Icon size={13} /> {label}
        </button>
    );

    const manifest = data.bts_capture_manifest || [];
    const paid = manifest.filter(m => m.paid_drop);
    const organic = manifest.filter(m => !m.paid_drop);
    const capturedCount = capturedItems.size;

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Top Bar */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-zinc-900/80 p-5 rounded-2xl border border-zinc-800">
                <div>
                    <h3 className="text-xl font-bold text-white">Social Media Playbook</h3>
                    <p className="text-zinc-500 text-[10px] mt-0.5 uppercase tracking-widest">
                        Andromeda Campaign — {manifest.length} Capture Directives · {paid.length} Paid Drops
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 bg-black/40 p-1.5 rounded-xl border border-zinc-800">
                    <SubTab id="timeline"   label="Timeline"     icon={CalendarDays} />
                    <SubTab id="manifest"   label="Capture List"  icon={Camera} />
                    <SubTab id="characters" label="Social Angles"  icon={Users} />
                    <SubTab id="paid"       label="Paid Strategy"  icon={DollarSign} />
                    <SubTab id="weekend"    label="War Room"       icon={Radio} />
                    <SubTab id="post90"     label="90-Day Plan"    icon={TrendingUp} />
                    <SubTab id="maintain"   label="Maintenance"    icon={Activity} />
                </div>
                <div className="flex flex-wrap gap-2">
                    <AppButton onClick={handleExportManifestCSV} variant="secondary" size="sm" icon={FileSpreadsheet} />
                    <AppButton onClick={handleExportCalendarJSON} variant="secondary" size="sm" icon={CalendarDays}>Calendar JSON</AppButton>
                    <AppButton onClick={handleCopyPlaybook} variant="secondary" size="sm" icon={Share2}>Copy</AppButton>
                    <AppButton onClick={handleExportPlaybookPDF} variant="primary" size="sm" icon={FileText}>PDF</AppButton>
                </div>
            </div>

            {/* ── TIMELINE ── */}
            {activeTab === 'timeline' && (
                <div className="space-y-4">
                    {(data.release_timeline || []).map((phase, pi) => (
                        <div key={pi} className="border border-zinc-800 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-3 px-5 py-4 bg-zinc-900/60 border-b border-zinc-800">
                                <CalendarDays size={14} className="text-amber-400 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-white text-sm">{phase.phase}</h4>
                                    <p className="text-[10px] text-zinc-400 mt-0.5 italic">{phase.strategic_goal}</p>
                                </div>
                                <span className="text-[10px] text-zinc-600 font-mono shrink-0">{phase.posts?.length || 0} posts</span>
                            </div>
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(phase.posts || []).map((post, ppi) => (
                                    <div key={ppi} className={`p-4 rounded-lg border transition-colors ${post.paid_promotion ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-800 bg-zinc-950'}`}>
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <p className="text-xs font-bold text-white leading-snug flex-1">{post.content_idea}</p>
                                            {post.paid_promotion && (
                                                <span className="text-[8px] font-black text-amber-400 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0 uppercase">PAID</span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            <FormatBadge format={post.format} />
                                            <HookBadge hook={post.andromeda_hook} />
                                        </div>
                                        <p className="text-[10px] text-zinc-500 italic leading-relaxed">{post.caption_direction}</p>
                                        {post.paid_promotion && post.paid_rationale && (
                                            <p className="text-[10px] text-amber-500/70 mt-1.5 leading-relaxed">
                                                <span className="font-bold not-italic">↑ Boost:</span> {post.paid_rationale}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    {!data.release_timeline?.length && (
                        <div className="p-10 text-center text-zinc-600 italic border border-dashed border-zinc-800 rounded-xl">No timeline generated.</div>
                    )}
                </div>
            )}

            {/* ── CAPTURE MANIFEST ── */}
            {activeTab === 'manifest' && (
                <div className="space-y-5">
                    {/* Progress bar */}
                    <div className="flex items-center gap-4 p-4 bg-zinc-900/40 rounded-xl border border-zinc-800">
                        <div className="flex-1">
                            <div className="flex justify-between text-[10px] text-zinc-500 mb-1.5">
                                <span>Capture Progress</span>
                                <span>{capturedCount} / {manifest.length}</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 transition-all" style={{ width: `${manifest.length ? (capturedCount / manifest.length) * 100 : 0}%` }} />
                            </div>
                        </div>
                        <span className="text-[10px] text-amber-400 font-mono font-bold">{manifest.length > 0 ? Math.round((capturedCount / manifest.length) * 100) : 0}%</span>
                    </div>

                    {/* Paid drops first */}
                    {paid.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <DollarSign size={12} className="text-amber-400" />
                                <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest">Paid-to-Promote Drops ({paid.length})</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {paid.map((item, i) => {
                                    const id = `paid-${i}`;
                                    const done = capturedItems.has(id);
                                    return (
                                        <div key={i} onClick={() => toggleCapture(id)}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all ${done ? 'bg-green-500/8 border-green-500/40 opacity-60' : 'bg-amber-500/5 border-amber-500/25 hover:border-amber-500/50'}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 ${done ? 'bg-green-500 border-green-500' : 'border-amber-500/50'}`}>
                                                    {done && <CheckCircle size={11} className="text-black" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-white mb-1.5 leading-snug">{item.moment}</p>
                                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                                        <span className="text-[9px] font-bold text-amber-400 border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 rounded-full">{item.capture_type}</span>
                                                        <HookBadge hook={item.andromeda_hook} />
                                                    </div>
                                                    <div className="flex gap-3 text-[10px] text-zinc-500">
                                                        <span>{item.emotional_register}</span>
                                                        <span>·</span>
                                                        <span>{item.posting_phase}</span>
                                                    </div>
                                                    <p className="text-[10px] text-zinc-500 mt-1.5 italic leading-relaxed">{item.caption_direction}</p>
                                                    {item.paid_drop_rationale && (
                                                        <p className="text-[10px] text-amber-500/70 mt-1.5 leading-relaxed">
                                                            <span className="font-bold not-italic">Boost rationale:</span> {item.paid_drop_rationale}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Organic captures */}
                    {organic.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Camera size={12} className="text-zinc-400" />
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Organic Capture ({organic.length})</h4>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {organic.map((item, i) => {
                                    const id = `organic-${i}`;
                                    const done = capturedItems.has(id);
                                    return (
                                        <div key={i} onClick={() => toggleCapture(id)}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all ${done ? 'bg-green-500/8 border-green-500/40 opacity-60' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-700'}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 ${done ? 'bg-green-500 border-green-500' : 'border-zinc-700'}`}>
                                                    {done && <CheckCircle size={11} className="text-black" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-white mb-1.5 leading-snug">{item.moment}</p>
                                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                                        <span className="text-[9px] font-bold text-zinc-400 border border-zinc-700 bg-zinc-800 px-2 py-0.5 rounded-full">{item.capture_type}</span>
                                                        <HookBadge hook={item.andromeda_hook} />
                                                    </div>
                                                    <div className="flex gap-3 text-[10px] text-zinc-500">
                                                        <span>{item.emotional_register}</span>
                                                        <span>·</span>
                                                        <span>{item.posting_phase}</span>
                                                    </div>
                                                    <p className="text-[10px] text-zinc-500 mt-1.5 italic">{item.caption_direction}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── CHARACTER SOCIAL ANGLES ── */}
            {activeTab === 'characters' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data.character_social_angles || []).map((char, i) => (
                        <div key={i} className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/20 transition-colors">
                            <div className="flex items-start justify-between gap-2 mb-3">
                                <div>
                                    <h4 className="font-bold text-white text-sm">{char.character_name}</h4>
                                    <p className="text-[10px] text-zinc-500 mt-0.5">{char.audience_segment_targeted}</p>
                                </div>
                                <FormatBadge format={char.best_format} />
                            </div>
                            <HookBadge hook={char.andromeda_hook_served} />
                            <p className="text-xs text-zinc-300 mt-3 mb-3 leading-relaxed">{char.social_angle}</p>
                            {char.signature_moment && (
                                <div className="bg-black/30 rounded-lg p-3 mb-3 border border-white/5">
                                    <p className="text-[9px] text-zinc-500 uppercase tracking-widest mb-1">Signature Moment</p>
                                    <p className="text-xs text-zinc-300 italic">{char.signature_moment}</p>
                                </div>
                            )}
                            <div className="space-y-1.5">
                                {(char.content_ideas || []).map((idea, j) => (
                                    <div key={j} className="flex gap-2 text-xs text-zinc-400">
                                        <span className="text-amber-500/60 font-bold shrink-0">{j+1}.</span>
                                        {idea}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    {!data.character_social_angles?.length && (
                        <div className="col-span-2 p-10 text-center text-zinc-600 italic border border-dashed border-zinc-800 rounded-xl">No character angles generated.</div>
                    )}
                </div>
            )}

            {/* ── PAID STRATEGY ── */}
            {activeTab === 'paid' && (
                <div className="space-y-5">
                    {/* Philosophy */}
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                        <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                            <Zap size={11} /> Andromeda Paid Philosophy
                        </h4>
                        <p className="text-sm text-zinc-300 leading-relaxed">{data.paid_promotion_strategy?.philosophy}</p>
                    </div>

                    {/* Budget Sequencing */}
                    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-5">
                        <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <BarChart2 size={11} /> Budget Sequencing
                        </h4>
                        <p className="text-sm text-zinc-300 leading-relaxed">{data.paid_promotion_strategy?.budget_sequencing}</p>
                    </div>

                    {/* Top Paid Drops */}
                    <div>
                        <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Top 5 Paid Drop Priorities</h4>
                        <div className="space-y-3">
                            {(data.paid_promotion_strategy?.top_paid_drops || []).map((drop, i) => (
                                <div key={i} className="flex gap-4 p-5 bg-zinc-950 border border-zinc-800 rounded-xl hover:border-amber-500/25 transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 font-black text-sm flex items-center justify-center shrink-0">
                                        {drop.rank}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-white text-sm mb-1">{drop.moment}</p>
                                        <p className="text-xs text-zinc-400 leading-relaxed mb-2">{drop.why}</p>
                                        <div className="flex flex-wrap gap-4 text-[10px]">
                                            <span className="text-amber-400 font-bold">
                                                <span className="text-zinc-600 font-normal">Daily budget: </span>{drop.suggested_daily_budget}
                                            </span>
                                            <span className="text-zinc-400">
                                                <span className="text-zinc-600">Watch: </span>{drop.target_metric}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Fatigue Protocol */}
                    {data.paid_promotion_strategy?.fatigue_protocol && (
                        <div className="bg-zinc-900/40 border border-red-500/20 rounded-xl p-5">
                            <h4 className="text-xs font-bold text-red-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <Activity size={11} /> Creative Fatigue Protocol
                            </h4>
                            <p className="text-sm text-zinc-300 leading-relaxed">{data.paid_promotion_strategy.fatigue_protocol}</p>
                        </div>
                    )}
                </div>
            )}

            {/* ── OPENING WEEKEND WAR ROOM ── */}
            {activeTab === 'weekend' && data.opening_weekend_plan && (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { label: 'Thursday Preview Night', key: 'thursday_preview', color: 'border-l-zinc-600' },
                            { label: 'Saturday Amplification', key: 'saturday_push',    color: 'border-l-amber-500' },
                            { label: 'Sunday Close',           key: 'sunday_close',     color: 'border-l-zinc-600' },
                        ].map(({ label, key, color }) => (
                            <div key={key} className={`p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl border-l-4 ${color}`}>
                                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">{label}</h4>
                                <p className="text-sm text-zinc-300 leading-relaxed">{data.opening_weekend_plan[key]}</p>
                            </div>
                        ))}

                        {/* Friday drops */}
                        <div className="p-5 bg-zinc-900/40 border border-amber-500/30 rounded-xl border-l-4 border-l-amber-500">
                            <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3">Friday Opening Day — Drop Sequence</h4>
                            <div className="space-y-2">
                                {(data.opening_weekend_plan.friday_drops || []).map((drop, i) => (
                                    <div key={i} className="flex gap-2.5 text-xs text-zinc-300">
                                        <span className="text-amber-500 font-bold shrink-0">{String(i+1).padStart(2,'0')}</span>
                                        {drop}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Real-time signals */}
                    {data.opening_weekend_plan.real_time_signals?.length > 0 && (
                        <div className="p-5 bg-zinc-950 border border-zinc-800 rounded-xl">
                            <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Radio size={11} className="text-amber-400" /> Andromeda Signals to Monitor
                            </h4>
                            <div className="space-y-2">
                                {data.opening_weekend_plan.real_time_signals.map((signal, i) => (
                                    <div key={i} className="flex gap-2.5 text-xs text-zinc-400">
                                        <span className="text-amber-500/60 shrink-0">›</span>
                                        {signal}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* ── 90-DAY POST-RELEASE PLAN ── */}
            {activeTab === 'post90' && (
                <div className="space-y-5">
                    {!data.post_release_90_day ? (
                        <div className="p-10 text-center border border-dashed border-zinc-800 rounded-xl text-zinc-600 italic">
                            Regenerate the Social Playbook to include the 90-Day Post-Release Plan.
                        </div>
                    ) : (
                        <>
                            <div className="p-5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                                <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <TrendingUp size={11} /> Post-Release Strategic Goal
                                </h4>
                                <p className="text-sm text-zinc-300 leading-relaxed">{data.post_release_90_day.strategic_goal}</p>
                            </div>

                            {[
                                { key: 'weeks_1_2', label: 'Weeks 1–2' },
                                { key: 'weeks_3_4', label: 'Weeks 3–4' },
                                { key: 'month_2',   label: 'Month 2' },
                                { key: 'month_3',   label: 'Month 3' },
                            ].map(({ key, label }) => {
                                const window = data.post_release_90_day[key];
                                if (!window) return null;
                                return (
                                    <div key={key} className="border border-zinc-800 rounded-xl overflow-hidden">
                                        <div className="flex items-center gap-3 px-5 py-4 bg-zinc-900/60 border-b border-zinc-800">
                                            <CalendarDays size={13} className="text-amber-400 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-white text-sm">{label}</h4>
                                                <p className="text-[10px] text-zinc-400 mt-0.5 italic">{window.focus}</p>
                                            </div>
                                            <span className="text-[10px] text-zinc-600 font-mono shrink-0">{window.posts?.length || 0} posts</span>
                                        </div>
                                        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {(window.posts || []).map((post, i) => (
                                                <div key={i} className={`p-4 rounded-lg border transition-colors ${post.paid_promotion ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-800 bg-zinc-950'}`}>
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <p className="text-xs font-bold text-white leading-snug flex-1">{post.content_idea}</p>
                                                        {post.paid_promotion && (
                                                            <span className="text-[8px] font-black text-amber-400 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded shrink-0 uppercase">PAID</span>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                                        <FormatBadge format={post.format} />
                                                        <HookBadge hook={post.andromeda_hook} />
                                                    </div>
                                                    <p className="text-[10px] text-zinc-500 italic leading-relaxed">{post.rationale}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            {data.post_release_90_day.vod_streaming_transition && (
                                <div className="p-5 bg-zinc-900/40 border border-blue-500/20 rounded-xl">
                                    <h4 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                                        <Radio size={11} /> VOD / Streaming Transition Strategy
                                    </h4>
                                    <p className="text-sm text-zinc-300 leading-relaxed">{data.post_release_90_day.vod_streaming_transition}</p>
                                </div>
                            )}

                            {data.post_release_90_day.long_tail_hooks?.length > 0 && (
                                <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Evergreen Long-Tail Hooks</h4>
                                    <div className="space-y-2">
                                        {data.post_release_90_day.long_tail_hooks.map((hook, i) => (
                                            <div key={i} className="flex gap-2.5 text-xs text-zinc-300">
                                                <span className="text-amber-500/60 font-bold shrink-0">{String(i+1).padStart(2,'0')}</span>
                                                {hook}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── MAINTENANCE SCHEDULE ── */}
            {activeTab === 'maintain' && (
                <div className="space-y-5">
                    {!data.maintenance_schedule ? (
                        <div className="p-10 text-center border border-dashed border-zinc-800 rounded-xl text-zinc-600 italic">
                            Regenerate the Social Playbook to include the Maintenance Schedule.
                        </div>
                    ) : (
                        <>
                            {/* Monthly cadence */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: 'Posts / Month',  value: data.maintenance_schedule.monthly_cadence?.minimum_posts_per_month },
                                    { label: 'Format Mix',     value: data.maintenance_schedule.monthly_cadence?.format_mix },
                                    { label: 'Paid Minimum',   value: data.maintenance_schedule.monthly_cadence?.paid_minimum },
                                    { label: 'Hook Rotation',  value: data.maintenance_schedule.monthly_cadence?.hook_rotation },
                                ].map(({ label, value }) => (
                                    <div key={label} className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                        <p className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest mb-1.5">{label}</p>
                                        <p className="text-sm font-medium text-white leading-snug">{value || '—'}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Performance review triggers */}
                            {data.maintenance_schedule.performance_review_triggers?.length > 0 && (
                                <div className="bg-zinc-900/40 border border-red-500/20 rounded-xl p-5">
                                    <h4 className="text-xs font-bold text-red-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <Activity size={11} /> Performance Review Triggers
                                    </h4>
                                    <div className="space-y-3">
                                        {data.maintenance_schedule.performance_review_triggers.map((trigger, i) => (
                                            <div key={i} className="flex gap-4 p-4 bg-black/30 rounded-lg border border-zinc-800">
                                                <AlertTriangle size={13} className="text-red-400 shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-xs font-bold text-zinc-200 mb-1">{trigger.signal}</p>
                                                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                                                        <span className="text-amber-400 font-bold">Action: </span>{trigger.action}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Content refresh schedule */}
                            {data.maintenance_schedule.content_refresh_schedule?.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <Sparkles size={11} className="text-amber-400" /> Creative Refresh Schedule
                                    </h4>
                                    <div className="space-y-3">
                                        {data.maintenance_schedule.content_refresh_schedule.map((refresh, i) => (
                                            <div key={i} className="flex gap-4 p-4 bg-zinc-950 border border-zinc-800 rounded-xl hover:border-amber-500/20 transition-colors">
                                                <div className="w-24 shrink-0">
                                                    <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">Timing</p>
                                                    <p className="text-xs font-bold text-amber-400">{refresh.timing}</p>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold text-white mb-1">{refresh.refresh_type}</p>
                                                    <p className="text-[11px] text-zinc-400 leading-relaxed">{refresh.andromeda_rationale}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Anniversary moments */}
                            {data.maintenance_schedule.anniversary_moments?.length > 0 && (
                                <div className="p-5 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                                    <h4 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <Zap size={11} /> Campaign Reactivation Milestones
                                    </h4>
                                    <div className="space-y-2">
                                        {data.maintenance_schedule.anniversary_moments.map((moment, i) => (
                                            <div key={i} className="flex gap-2.5 text-xs text-zinc-300">
                                                <span className="text-amber-500/60 shrink-0">›</span>
                                                {moment}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Evergreen assets */}
                            {data.maintenance_schedule.evergreen_asset_list?.length > 0 && (
                                <div className="p-5 bg-zinc-900/40 border border-zinc-800 rounded-xl">
                                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">Evergreen Assets — Keep In Rotation</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {data.maintenance_schedule.evergreen_asset_list.map((asset, i) => (
                                            <div key={i} className="flex gap-2.5 p-3 bg-zinc-950 border border-zinc-800 rounded-lg text-xs text-zinc-300">
                                                <CheckCircle size={12} className="text-green-500 shrink-0 mt-0.5" />
                                                {asset}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

        </div>
    );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CampaignIntelligenceTab = ({ 
    filmData, 
    setNotification, 
    saveFilmData, 
    credits, 
    deductCredit,
    storage
}) => {
    const [analysis, setAnalysis]                 = useState(filmData?.script_analysis || null);
    const [characterAnalysis, setCharacterAnalysis] = useState(filmData?.character_analysis || null);
    const [socialPlaybook, setSocialPlaybook]     = useState(filmData?.social_playbook || null);

    const [activeTab, setActiveTab]           = useState('story');
    const [loading, setLoading]               = useState(false);
    const [loadingMode, setLoadingMode]       = useState('script');
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);
    const [scriptContent, setScriptContent]   = useState(filmData?.script || '');
    const fileInputRef = useRef(null);

    // Pull the latest Andromeda analysis from filmData
    const audienceAnalysis = filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis || null;

    useEffect(() => {
        if (filmData?.script_analysis)  setAnalysis(filmData.script_analysis);
        if (filmData?.character_analysis) setCharacterAnalysis(filmData.character_analysis);
        if (filmData?.social_playbook)  setSocialPlaybook(filmData.social_playbook);
        if (filmData?.script)           setScriptContent(filmData.script);
    }, [filmData]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setNotification({ type: 'error', message: 'File too large. Limit is 10MB.' }); return;
        }
        setNotification({ type: 'info', message: 'Reading script file...' });
        try {
            const text = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = reject;
                reader.readAsText(file);
            });
            setScriptContent(text);
            await saveFilmData({ ...filmData, script: text });
            setNotification({ type: 'success', message: 'Script loaded.' });
        } catch { setNotification({ type: 'error', message: 'Failed to read file.' }); }
    };

    // Individual per-section costs still exist under the hood (Story 5cr, Characters 3cr,
    // Playbook 5cr) so a partial regeneration knows what to charge, but the primary path
    // is now one action that runs all three and produces a single Social Plan.
    const SECTION_COST = { script: 5, character: 3, social: 5 };
    // Character Profiler is no longer part of the automatic Generate Campaign
    // run — it wasn't pulling its weight in the output. Story + Playbook only.
    const GENERATE_CAMPAIGN_COST = SECTION_COST.script + SECTION_COST.social;

    const runSection = async (mode) => {
        if (mode === 'script') {
            const result = await generateEnhancedScriptAnalysisLLM(scriptContent, filmData, audienceAnalysis, credits, deductCredit, setNotification);
            setAnalysis(result);
            return { key: 'script_analysis', result };
        }
        if (mode === 'character') {
            const result = await generateCharacterAnalysisLLM(scriptContent, [], filmData, credits, deductCredit, setNotification, audienceAnalysis);
            setCharacterAnalysis(result);
            return { key: 'character_analysis', result };
        }
        // 'social' — this LLM call doesn't deduct internally like the other two, so we do it here
        const result = await generateSocialMediaPlaybookLLM(filmData, audienceAnalysis, scriptContent);
        await deductCredit(SECTION_COST.social);
        setSocialPlaybook(result);
        return { key: 'social_playbook', result };
    };

    // Single entry point for the Social Plan: Story & Hooks -> Social Playbook,
    // saved once as one unified generation. Character Profiler used to run here
    // too but wasn't earning its cost in the output — it's still available as a
    // manual regen below for anyone who wants it, just not part of the default run.
    const generateCampaign = async () => {
        if (!scriptContent || scriptContent.trim().length < 100) {
            setNotification({ type: 'error', message: 'Script required. Upload a file first.' }); return;
        }
        if (!audienceAnalysis) {
            setNotification({ type: 'error', message: 'Run Audience Genius first — Andromeda hooks are required to generate the campaign.' });
            return;
        }
        if (credits < GENERATE_CAMPAIGN_COST) {
            setNotification({ type: 'error', message: `Insufficient credits. Generate Campaign requires ${GENERATE_CAMPAIGN_COST}.` });
            return;
        }

        setLoading(true);
        setLoadingStepIndex(0);
        const interval = setInterval(() => setLoadingStepIndex(prev => Math.min(prev + 1, 4)), 1800);

        try {
            const updates = {};

            setLoadingMode('script');
            const story = await runSection('script');
            updates[story.key] = story.result;

            setLoadingMode('social');
            const playbook = await runSection('social');
            updates[playbook.key] = playbook.result;

            // One save, one Social Plan generation event — not three disconnected writes.
            await saveFilmData({
                ...filmData,
                ...updates,
                social_plan_generated_at: new Date().toISOString()
            });

            setActiveTab('story');
            setNotification({ type: 'success', message: 'Social Plan generated — Story, Characters & Playbook are ready.' });
        } catch (e) {
            console.error(e);
            setNotification({ type: 'error', message: e.message || 'Campaign generation failed partway through — check credits and re-run.' });
        } finally {
            clearInterval(interval);
            setLoading(false);
        }
    };

    // Kept for power users who want to regenerate a single section (e.g. the script
    // changed) without re-running the whole campaign and re-spending all three costs.
    const runAnalysis = async (mode) => {
        if (!scriptContent || scriptContent.trim().length < 100) {
            setNotification({ type: 'error', message: 'Script required. Upload a file first.' }); return;
        }
        const cost = SECTION_COST[mode];
        if (credits < cost) {
            setNotification({ type: 'error', message: `Insufficient credits. Requires ${cost}.` }); return;
        }
        if ((mode === 'script' || mode === 'social') && !audienceAnalysis) {
            setNotification({ type: 'error', message: 'Run Audience Genius first — Andromeda hooks are required for this analysis.' });
            return;
        }

        setLoading(true);
        setLoadingMode(mode);
        setLoadingStepIndex(0);
        const interval = setInterval(() => setLoadingStepIndex(prev => Math.min(prev + 1, 4)), 1800);

        try {
            const { key, result } = await runSection(mode);
            await saveFilmData({ ...filmData, [key]: result });
            setActiveTab(mode === 'script' ? 'story' : mode === 'character' ? 'characters' : 'playbook');
            setNotification({ type: 'success', message: 'Section regenerated.' });
        } catch (e) {
            console.error(e);
            setNotification({ type: 'error', message: e.message || 'Analysis failed.' });
        } finally {
            clearInterval(interval);
            setLoading(false);
        }
    };

    const hasAndromeda = !!audienceAnalysis;
    const hasFullCampaign = !!(analysis && socialPlaybook);

    // The comprehensive Generate Campaign export: Audience Genius + Story & Hooks +
    // the Social Playbook, compiled into one file — audience_intelligence section
    // plus the same phases/projects/posts calendar shape the Campaign Engine's
    // "Add strategy brief" importer already accepts, so this drops straight in.
    const handleExportCampaignJSON = () => {
        try {
            const compiled = compileCampaignExportJSON(filmData, audienceAnalysis, analysis, socialPlaybook);
            const blob = new Blob([JSON.stringify(compiled, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${(filmData?.title || 'campaign').replace(/\s+/g, '_')}_campaign_export.json`);
            document.body.appendChild(link); link.click(); document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setNotification({ type: 'success', message: `Campaign exported — ${compiled._meta.total_posts} posts, ready to import into the Campaign Engine.` });
        } catch (e) {
            setNotification({ type: 'error', message: e.message || 'Campaign export failed.' });
        }
    };

    return (
        <div className="h-full flex flex-col">
            <IntelligenceOverlay isVisible={loading} mode={loadingMode} currentStepIndex={loadingStepIndex} />

            {/* Header */}
            <div className="flex justify-between items-end mb-6 pb-6 border-b border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-900 rounded-xl flex items-center justify-center border border-zinc-800">
                        <BookOpen size={22} className="text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">Generate Campaign</h2>
                        <p className="text-xs text-zinc-500 uppercase tracking-widest">
                            {hasAndromeda ? 'Andromeda-Powered · One run builds your Social Plan' : 'Run Audience Genius first to unlock Andromeda mode'}
                        </p>
                    </div>
                </div>
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className="group px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-500 rounded-lg cursor-pointer transition-all flex items-center gap-2.5"
                >
                    <Upload size={14} className="text-zinc-400 group-hover:text-amber-400" />
                    <span className="text-xs font-bold text-zinc-300 group-hover:text-white uppercase tracking-wide">
                        {scriptContent ? 'Replace Script' : 'Upload Script'}
                    </span>
                    <span className={`text-[10px] font-mono ml-1 ${scriptContent ? 'text-green-400' : 'text-zinc-600'}`}>
                        {scriptContent ? '● LOADED' : '○ MISSING'}
                    </span>
                    <input ref={fileInputRef} type="file" accept=".txt,.pdf" onChange={handleFileUpload} className="hidden" />
                </div>
            </div>

            {/* Andromeda gate notice */}
            {!hasAndromeda && (
                <div className="mb-5 flex items-center gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                    <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                    <p className="text-xs text-amber-300">
                        <span className="font-bold">Andromeda hooks not loaded.</span> Go to Audience Genius and run an analysis first. The Creative Matrix powers the whole campaign.
                    </p>
                </div>
            )}

            {/* Primary action: one button, one Social Plan */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
                <button onClick={generateCampaign} disabled={loading || !hasAndromeda}
                    className="group flex-1 flex items-center justify-center gap-3 py-4 px-4 bg-amber-500 hover:bg-amber-400 rounded-xl text-sm font-bold uppercase tracking-widest text-black transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Zap size={16} className={loading ? 'animate-pulse' : 'fill-current'} />
                    {loading ? 'Generating Campaign…' : hasFullCampaign ? 'Regenerate Full Campaign' : 'Generate Campaign'}
                    <span className="font-normal normal-case tracking-normal opacity-70">({GENERATE_CAMPAIGN_COST} cr — Story &amp; Social Playbook)</span>
                </button>
                <button
                    onClick={handleExportCampaignJSON}
                    disabled={loading || !hasFullCampaign || !filmData?.releaseDate}
                    title={!filmData?.releaseDate ? 'Set a Target Release Date in Audience Genius first' : ''}
                    className="group flex items-center justify-center gap-2.5 py-4 px-6 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-purple-500 rounded-xl text-sm font-bold uppercase tracking-widest text-zinc-200 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FileText size={16} className="group-hover:text-purple-400" />
                    Export Campaign JSON
                </button>
            </div>
            {hasFullCampaign && !filmData?.releaseDate && (
                <p className="text-[11px] text-amber-400/80 -mt-2 mb-5">
                    Export needs a Target Release Date — every phase in the calendar anchors to it. Set it in Audience Genius.
                </p>
            )}

            {/* Secondary: regenerate a single section without re-running the whole campaign */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-7">
                <button onClick={() => runAnalysis('script')} disabled={loading || !hasAndromeda}
                    className="group flex items-center justify-center gap-2 py-2.5 px-4 bg-zinc-900/40 border border-zinc-800/70 rounded-lg text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white hover:border-amber-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Layers size={12} className="group-hover:text-amber-400" /> Regen Story <span className="text-zinc-600 font-normal normal-case tracking-normal">({SECTION_COST.script} cr)</span>
                </button>
                <button onClick={() => runAnalysis('character')} disabled={loading}
                    className="group flex items-center justify-center gap-2 py-2.5 px-4 bg-zinc-900/40 border border-zinc-800/70 rounded-lg text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white hover:border-amber-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <UserCircle size={12} className="group-hover:text-amber-400" /> Regen Characters <span className="text-zinc-600 font-normal normal-case tracking-normal">({SECTION_COST.character} cr)</span>
                </button>
                <button onClick={() => runAnalysis('social')} disabled={loading || !hasAndromeda}
                    className="group flex items-center justify-center gap-2 py-2.5 px-4 bg-zinc-900/40 border border-zinc-800/70 rounded-lg text-[11px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white hover:border-amber-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <Megaphone size={12} className="group-hover:text-amber-400" /> Regen Playbook <span className="text-zinc-600 font-normal normal-case tracking-normal">({SECTION_COST.social} cr)</span>
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800 mb-6">
                <NavTab label="Story & Hooks"      icon={Sparkles}   isActive={activeTab === 'story'}      onClick={() => setActiveTab('story')} />
                <NavTab label="Character Profiler" icon={Users}      isActive={activeTab === 'characters'} onClick={() => setActiveTab('characters')} />
                <NavTab label="Social Playbook"    icon={Megaphone}  isActive={activeTab === 'playbook'}   onClick={() => setActiveTab('playbook')} />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pb-20">
                {activeTab === 'story'      && <StoryIntelView      analysis={analysis}          filmTitle={filmData?.title} setNotification={setNotification} />}
                {activeTab === 'characters' && <CharacterIntelView  data={characterAnalysis}     filmTitle={filmData?.title} setNotification={setNotification} />}
                {activeTab === 'playbook'   && <SocialPlaybookView  data={socialPlaybook}         filmTitle={filmData?.title} filmData={filmData} audienceAnalysis={audienceAnalysis} setNotification={setNotification} />}
            </div>
        </div>
    );
    };
export default CampaignIntelligenceTab;